import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNav from '@/components/AdminNav';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { FileText, Download, Eye, Trash2, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AdminJSAs = ({ user, setUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submittedJsas, setSubmittedJsas] = useState([]);
  const [showJsaDialog, setShowJsaDialog] = useState(false);
  const [selectedJsa, setSelectedJsa] = useState(null);
  const [loadingJsas, setLoadingJsas] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadSubmittedJsas();
  }, []);

  const loadSubmittedJsas = async () => {
    setLoadingJsas(true);
    try {
      const response = await axiosInstance.get('/jsa');
      setSubmittedJsas(response.data);
    } catch (error) {
      console.error('Failed to load JSAs:', error);
      toast.error('Failed to load JSAs');
    } finally {
      setLoadingJsas(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  const handleViewJsa = (jsa) => {
    console.log('Opening JSA view dialog:', jsa.id, jsa.taskDescription);
    setSelectedJsa(jsa);
    setShowJsaDialog(true);
  };

  const handleDownloadJsaPdf = async (jsaId, taskDescription) => {
    try {
      console.log('Downloading PDF for JSA:', jsaId, 'Task:', taskDescription);
      const timestamp = new Date().getTime();
      const response = await axiosInstance.get(`/jsa/${jsaId}/generate-pdf?t=${timestamp}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileName = `JSA_${taskDescription.replace(/[^a-z0-9]/gi, '_').substring(0, 30)}.pdf`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('common.pdfDownloaded'));
    } catch (error) {
      console.error('Failed to download JSA PDF:', error);
      toast.error(t('common.pdfFailed'));
    }
  };

  const handleDeleteJsa = async (jsaId, taskDescription) => {
    if (!window.confirm(t('common.confirmDelete'))) {
      return;
    }
    try {
      console.log('Deleting JSA:', jsaId);
      await axiosInstance.delete(`/jsa/${jsaId}`);
      toast.success(t('common.deleteSuccess'));
      setShowJsaDialog(false);
      loadSubmittedJsas();
    } catch (error) {
      console.error('Failed to delete JSA:', error);
      toast.error(error.response?.data?.detail || t('common.deleteFailed'));
    }
  };

  const filteredJsas = submittedJsas.filter(jsa =>
    jsa.taskDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    jsa.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    jsa.submittedBy?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="mb-6 gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </Button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('jsa.title')}</h1>
          <p className="text-gray-600 mt-1">{t('jsa.subtitle')}</p>
        </div>

        {/* JSAs Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t('jsa.allJsas')}</CardTitle>
                <CardDescription>{t('jsa.subtitle')}</CardDescription>
              </div>
              <Button onClick={loadSubmittedJsas} variant="outline" size="sm">
                {t('common.refresh')}
              </Button>
            </div>
            <div className="mt-4">
              <Input
                placeholder={t('jsa.searchJsas')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-md"
              />
            </div>
          </CardHeader>
          <CardContent>
            {loadingJsas ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : filteredJsas.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>{t('jsa.noJsas')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredJsas.map((jsa) => (
                  <div
                    key={jsa.id}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleViewJsa(jsa)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg text-gray-900">{jsa.taskDescription}</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Location: {jsa.location} | Project: {jsa.project || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Submitted by: {jsa.submittedBy} on {new Date(jsa.createdAt).toLocaleDateString()}
                        </p>
                        {jsa.aiEnhanced && (
                          <Badge className="mt-2 bg-purple-100 text-purple-700 border-purple-200" variant="outline">
                            AI Enhanced
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadJsaPdf(jsa.id, jsa.taskDescription);
                          }}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewJsa(jsa);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Update Queue Tab section removed */}

        {/* JSA Details Dialog */}
        <Dialog open={showJsaDialog} onOpenChange={setShowJsaDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            {selectedJsa && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    JSA: {selectedJsa.taskDescription}
                  </DialogTitle>
                  <DialogDescription>
                    Submitted by {selectedJsa.submittedBy} on {new Date(selectedJsa.createdAt).toLocaleDateString()}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">{t('common.location')}</p>
                      <p className="font-medium">{selectedJsa.location}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Project</p>
                      <p className="font-medium">{selectedJsa.project || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Crew Size</p>
                      <p className="font-medium">{selectedJsa.crewSize || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{t('common.date')}</p>
                      <p className="font-medium">{new Date(selectedJsa.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>

                  {/* Task Steps */}
                  {selectedJsa.tasks && selectedJsa.tasks.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-900 mb-2">{t('jsa.stepsAndHazards')}:</p>
                      <div className="space-y-2">
                        {selectedJsa.tasks.map((task, index) => (
                          <div key={index} className="bg-gray-50 p-3 rounded border">
                            <p className="font-medium text-gray-900">Step {index + 1}: {task.step}</p>
                            <p className="text-sm text-red-600 mt-1"><strong>Hazard:</strong> {task.hazard}</p>
                            <p className="text-sm text-green-600 mt-1"><strong>Control:</strong> {task.control}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* PPE Required */}
                  {selectedJsa.ppeRequired && selectedJsa.ppeRequired.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-900 mb-2">{t('jsa.ppe')}:</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedJsa.ppeRequired.map((ppe, index) => (
                          <Badge key={index} variant="outline">{ppe}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI Enhancement Badge */}
                  {selectedJsa.aiEnhanced && (
                    <div className="bg-purple-50 p-3 rounded border border-purple-200">
                      <p className="text-sm text-purple-800">
                        ✨ This JSA has been enhanced with AI-powered safety insights
                      </p>
                    </div>
                  )}
                </div>

                <DialogFooter className="flex justify-between">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteJsa(selectedJsa.id, selectedJsa.taskDescription)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    {t('common.delete')}
                  </Button>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleDownloadJsaPdf(selectedJsa.id, selectedJsa.taskDescription)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      {t('jsa.downloadPdf')}
                    </Button>
                    <Button variant="outline" onClick={() => setShowJsaDialog(false)}>
                      {t('common.close')}
                    </Button>
                  </div>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminJSAs;
